import { useId, useMemo, useState, type MouseEvent } from 'react';
import type { TagColor } from '../../atoms/Tag';

export type TagPickerOption = {
  id: string;
  name: string;
  color?: TagColor;
};

export type TagPickerProps<T extends TagPickerOption = TagPickerOption> = {
  /** Available options to select from */
  options: T[];
  /** Currently selected options */
  value: T[];
  /** Callback invoked when selection changes */
  onChange: (value: T[]) => void;
  /** Optional callback to create a new tag on the fly */
  onCreateOption?: (name: string) => Promise<T | void> | T | void;
  /** Text on the trigger button. Default: 'Ajouter / modifier des tags' */
  triggerButtonText?: string;
  /** Whether to render selected tag badges inline beside the trigger button. Default: true */
  showSelectedTagsInline?: boolean;
  /** Color applied to selected tags. Default: 'providers' */
  tagColor?: TagColor;
  /** Placeholder for the search input. Default: 'Rechercher ou créer un tag…' */
  searchPlaceholder?: string;
  /** Title inside the popover. Default: 'Attribuer des tags' */
  title?: string;
  /** Message displayed when no tag matches the search and creation is not possible. Default: 'Aucun tag trouvé' */
  emptyText?: string;
  /** Whether the options are loading */
  loading?: boolean;
  /** Whether tag creation is in progress */
  isCreating?: boolean;
  /** Whether the picker is disabled */
  disabled?: boolean;
  'data-testid'?: string;
};

export function useTagPicker<T extends TagPickerOption = TagPickerOption>(props: TagPickerProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [localCreating, setLocalCreating] = useState(false);
  const searchInputId = useId();

  const isOpen = Boolean(anchorEl);

  function handleOpen(event: MouseEvent<HTMLElement>) {
    if (props.disabled) return;
    setAnchorEl(event.currentTarget);
    setSearchTerm('');
    setErrorMessage(null);
  }

  function handleClose() {
    setAnchorEl(null);
    setSearchTerm('');
    setErrorMessage(null);
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (errorMessage) {
      setErrorMessage(null);
    }
  }

  const selectedIds = useMemo(
    () => new Set(props.value.map((item) => item.id)),
    [props.value]
  );

  const trimmedSearch = searchTerm.trim();

  const filteredOptions = useMemo(() => {
    if (!trimmedSearch) return props.options;
    const lower = trimmedSearch.toLowerCase();
    return props.options.filter((opt) => opt.name.toLowerCase().includes(lower));
  }, [props.options, trimmedSearch]);

  const exactMatchExists = useMemo(() => {
    if (!trimmedSearch) return true;
    const lower = trimmedSearch.toLowerCase();
    return props.options.some((opt) => opt.name.toLowerCase() === lower);
  }, [props.options, trimmedSearch]);

  const canCreate = Boolean(
    props.onCreateOption && trimmedSearch && !exactMatchExists
  );

  function handleToggleOption(option: T) {
    if (selectedIds.has(option.id)) {
      props.onChange(props.value.filter((item) => item.id !== option.id));
    } else {
      props.onChange([...props.value, option]);
    }
  }

  async function handleCreateOption() {
    if (!canCreate || !props.onCreateOption || !trimmedSearch) return;

    try {
      setLocalCreating(true);
      setErrorMessage(null);
      const created = await props.onCreateOption(trimmedSearch);
      if (created) {
        props.onChange([...props.value, created]);
      }
      setSearchTerm('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la création du tag';
      setErrorMessage(msg);
    } finally {
      setLocalCreating(false);
    }
  }

  return {
    isOpen,
    anchorEl,
    handleOpen,
    handleClose,
    searchTerm,
    setSearchTerm,
    handleSearchChange,
    errorMessage,
    searchInputId,
    filteredOptions,
    selectedIds,
    handleToggleOption,
    handleCreateOption,
    canCreate,
    trimmedSearch,
    isCreating: props.isCreating ?? localCreating,
  };
}
